import { useState, useRef, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  reauthenticateWithCredential,
  EmailAuthProvider,
  updatePassword,
  signInWithPopup,
  OAuthProvider,
  GoogleAuthProvider,
} from 'firebase/auth';
import type { User } from 'firebase/auth';
import type { Timestamp } from 'firebase/firestore';
import { motion } from 'framer-motion';
import { useAuthStore } from '../stores/authStore';
import type { Profile } from '../types/user';
import { auth } from '../lib/firebase';
import AppFooter from '../components/shared/AppFooter';
import { renameExplorerName, requestEmailChange, updateProfile } from '../lib/functions';
import { signOut } from '../hooks/useAuth';
import { AVATARS } from '../lib/avatars';
import {
  sendUserMessage,
  subscribeToUserThread,
  markUserRead,
} from '../lib/adminMessaging';
import type { ThreadMessage } from '../lib/adminMessaging';
import { moderateText } from '../utils/contentModeration';

const EXPLORER_NAME_COOLDOWN_DAYS = 30;
const googleProvider = new GoogleAuthProvider();
const appleProvider = new OAuthProvider('apple.com');

function sessionAgeMs(): number {
  const lastSignIn = auth.currentUser?.metadata.lastSignInTime;
  if (!lastSignIn) return Infinity;
  return Date.now() - new Date(lastSignIn).getTime();
}

function checkIsUnder13(dob: Timestamp | undefined): boolean {
  if (!dob) return false;
  return Date.now() - dob.toDate().getTime() < 13 * 365.25 * 24 * 60 * 60 * 1000;
}

type StatusMsg = { type: 'success' | 'error'; text: string } | null;
type Section = 'name' | 'password' | 'email' | 'sidekick' | null;

type ToneOption = 'cheerful' | 'chill' | 'curious' | 'coach';

const TONE_OPTIONS: { tone: ToneOption; label: string; sample: string; emoji: string }[] = [
  { tone: 'cheerful', label: 'Cheerful', sample: "Let's goooo! 🎉", emoji: '🎉' },
  { tone: 'chill', label: 'Chill', sample: 'No worries, we got this 😎', emoji: '😎' },
  { tone: 'curious', label: 'Curious', sample: 'Hmm, interesting... 🤔', emoji: '🤔' },
  { tone: 'coach', label: 'Coach', sample: "Let's level up! 💪", emoji: '💪' },
];

// ─── Shared section shell ──────────────────────────────────────────
interface SectionShellProps {
  title: string;
  summary: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function SectionShell({ title, summary, open, onToggle, children }: SectionShellProps) {
  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-surface/60">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-5 py-4 text-left"
      >
        <div>
          <p className="font-marker text-lg text-amber">{title}</p>
          <p className="font-gill text-sm text-ink/60">{summary}</p>
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div className="border-t border-white/10 px-5 pb-5 pt-4">
          {children}
        </div>
      )}
    </div>
  );
}

const inputCls =
  'block w-full rounded-2xl border border-white/10 bg-base px-4 py-3 font-gill text-sm text-ink placeholder:text-ink/40 focus:border-amber/50 focus:outline-none';

function EyeIcon({ visible }: { visible: boolean }) {
  if (visible) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </svg>
    );
  }
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

// ─── Chat Modal ────────────────────────────────────────────────────────────────
interface ChatModalProps {
  userId: string;
  explorerName: string;
  onClose: () => void;
}

function ChatModal({ userId, explorerName, onClose }: ChatModalProps) {
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [permissionDenied, setPermissionDenied] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    markUserRead(userId).catch(() => {});
    return subscribeToUserThread(
      userId,
      (msgs) => {
        setMessages(msgs);
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
      },
      (err) => {
        if (err.message?.includes('permission') || (err as { code?: string }).code === 'permission-denied') {
          setPermissionDenied(true);
        }
      },
    );
  }, [userId]);

  async function handleSend() {
    const text = input.trim();
    if (!text || sending) return;
    const modResult = moderateText(text);
    if (!modResult.passed) {
      setSendError(modResult.message ?? 'Please use appropriate language.');
      return;
    }
    setSending(true);
    setSendError('');
    try {
      await sendUserMessage(userId, explorerName, text);
      setInput('');
    } catch (err: unknown) {
      const code = (err as { code?: string }).code;
      if (code === 'permission-denied') {
        setPermissionDenied(true);
      } else {
        setSendError('Failed to send. Please try again.');
      }
    } finally {
      setSending(false);
    }
  }

  function timeAgo(ts: { toDate: () => Date } | null): string {
    if (!ts) return '';
    const diff = Date.now() - ts.toDate().getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ background: '#0D0F1C' }}>
      {/* Header */}
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-white/10 px-5">
        <button
          onClick={onClose}
          className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/5 text-ink/60 hover:bg-white/10"
          aria-label="Close chat"
        >
          ←
        </button>
        <div>
          <p className="font-marker text-lg text-amber">OMore Team</p>
          <p className="font-gill text-xs text-ink/40">We usually reply within 24 hours</p>
        </div>
      </header>

      {/* Permission denied banner */}
      {permissionDenied && (
        <div className="mx-5 mt-4 rounded-2xl border border-coral/30 bg-coral/10 px-4 py-3">
          <p className="font-marker text-sm text-coral">Messaging not available</p>
          <p className="mt-1 font-gill text-xs text-ink/60">
            There's a configuration issue on our end. Please contact us at{' '}
            <span className="font-semibold text-ink">support@omore.app</span> for help.
          </p>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {!permissionDenied && messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center py-16 text-center">
            <span className="text-5xl">🦊</span>
            <p className="mt-3 font-marker text-lg text-ink/50">Say hello!</p>
            <p className="mt-1 font-gill text-sm text-ink/30">We're here to help with anything.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.fromAdmin ? 'justify-start' : 'justify-end'}`}>
                <div className="max-w-[75%]">
                  <p className={`mb-0.5 font-gill text-[10px] ${msg.fromAdmin ? 'text-ink/40' : 'text-right text-amber/60'}`}>
                    {msg.fromAdmin ? (msg.senderName || 'OMore Team') : 'You'}
                  </p>
                  <div
                    className="rounded-2xl px-4 py-2.5 font-gill text-sm leading-relaxed"
                    style={
                      msg.fromAdmin
                        ? { background: 'rgba(255,255,255,0.07)', color: 'rgba(232,234,240,0.9)' }
                        : { background: '#F59E0B', color: '#000' }
                    }
                  >
                    {msg.content}
                  </div>
                  {msg.createdAt && (
                    <p className={`mt-0.5 font-gill text-[10px] text-ink/25 ${msg.fromAdmin ? '' : 'text-right'}`}>
                      {timeAgo(msg.createdAt)}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      {!permissionDenied && (
        <div className="shrink-0 border-t border-white/10 px-5 py-3">
          {sendError && (
            <p className="mb-2 font-gill text-xs text-coral">{sendError}</p>
          )}
          <div className="flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => { setInput(e.target.value); setSendError(''); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={sending}
              placeholder="Type a message…"
              rows={2}
              className="max-h-32 flex-1 resize-none rounded-2xl border border-white/10 bg-white/5 px-4 py-2.5 font-gill text-sm text-ink placeholder:text-ink/30 focus:border-amber/40 focus:outline-none disabled:opacity-40"
            />
            <button
              onClick={handleSend}
              disabled={sending || !input.trim()}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-all disabled:opacity-30"
              style={{ background: '#F59E0B' }}
              aria-label="Send message"
            >
              <svg className="h-4 w-4 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function AccountPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.profile);
  const loading = useAuthStore((s) => s.loading);
  const setProfile = useAuthStore((s) => s.setProfile);

  const currentUser = auth.currentUser;
  const isPasswordProvider = !!currentUser?.providerData.some((p) => p.providerId === 'password');
  const oauthProviderId = currentUser?.providerData.find((p) => p.providerId !== 'password')?.providerId;
  const under13 = checkIsUnder13(profile?.dateOfBirth);

  const [openSection, setOpenSection] = useState<Section>(null);
  const [showChat, setShowChat] = useState(false);
  const toggle = (s: Section) => setOpenSection((prev) => (prev === s ? null : s));

  // ── Reauth gate ──────────────────────────────────────────────────────────────
  const [needsReauth, setNeedsReauth] = useState(false);
  const [reauthPw, setReauthPw] = useState('');
  const [reauthError, setReauthError] = useState('');
  const [reauthLoading, setReauthLoading] = useState(false);
  const pendingActionRef = useRef<(() => Promise<void>) | null>(null);

  async function withReauth(action: () => Promise<void>): Promise<void> {
    if (sessionAgeMs() > 5 * 60 * 1000) {
      pendingActionRef.current = action;
      setNeedsReauth(true);
      return;
    }
    await action();
  }

  async function handleReauth() {
    if (!currentUser) return;
    setReauthError('');
    setReauthLoading(true);
    try {
      if (isPasswordProvider) {
        const credential = EmailAuthProvider.credential(currentUser.email!, reauthPw);
        await reauthenticateWithCredential(currentUser, credential);
      } else {
        const provider = oauthProviderId === 'apple.com' ? appleProvider : googleProvider;
        await signInWithPopup(auth, provider);
      }
      setNeedsReauth(false);
      setReauthPw('');
      const action = pendingActionRef.current;
      pendingActionRef.current = null;
      if (action) await action();
    } catch (err: unknown) {
      setReauthError(err instanceof Error ? err.message : 'Re-authentication failed.');
    } finally {
      setReauthLoading(false);
    }
  }

  function dismissReauth() {
    setNeedsReauth(false);
    setReauthPw('');
    setReauthError('');
    pendingActionRef.current = null;
  }

  async function handleSignOut() {
    await signOut();
    navigate('/', { replace: true });
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-base">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-amber border-t-transparent" />
      </div>
    );
  }

  if (!user || !profile) {
    return <Navigate to="/auth" replace />;
  }

  const displayName = profile.explorerName || profile.displayName || '';

  return (
    <>
      <div className="min-h-screen bg-base text-ink" style={{ background: 'radial-gradient(ellipse at top left, #1a2e1a 0%, #0D0F1C 40%)' }}>
        <div className="mx-auto max-w-2xl px-5 py-8">

          <div className="mb-2 flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 text-ink/70 transition-colors hover:bg-white/10"
              aria-label="Go back"
            >
              ←
            </button>
          </div>
          <h1 className="font-marker text-4xl text-amber md:text-5xl">Account Settings</h1>
          {displayName && (
            <p className="mt-1 font-gill text-sm text-ink/60">
              Signed in as <span className="font-semibold text-ink">{displayName}</span>
            </p>
          )}

          {needsReauth && (
            <div className="mt-6 rounded-2xl border border-amber/30 bg-amber/5 p-5">
              <h2 className="font-marker text-lg text-amber">Confirm your identity</h2>
              <p className="mt-1 font-gill text-sm text-ink/60">
                For your security, please re-authenticate before making changes.
              </p>
              {isPasswordProvider ? (
                <div className="mt-4 space-y-3">
                  <input
                    type="password"
                    value={reauthPw}
                    onChange={(e) => setReauthPw(e.target.value)}
                    placeholder="Your current password"
                    className={inputCls}
                    onKeyDown={(e) => e.key === 'Enter' && handleReauth()}
                    autoFocus
                  />
                  <button
                    onClick={handleReauth}
                    disabled={reauthLoading || !reauthPw}
                    className="rounded-2xl bg-amber px-5 py-2.5 font-marker text-sm text-base transition-opacity disabled:opacity-50"
                  >
                    {reauthLoading ? 'Confirming…' : 'Confirm'}
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleReauth}
                  disabled={reauthLoading}
                  className="mt-4 rounded-2xl bg-amber px-5 py-2.5 font-marker text-sm text-base transition-opacity disabled:opacity-50"
                >
                  {reauthLoading ? 'Confirming…' : `Re-authenticate with ${oauthProviderId === 'apple.com' ? 'Apple' : 'Google'}`}
                </button>
              )}
              {reauthError && <p className="mt-2 font-gill text-sm text-coral">{reauthError}</p>}
              <button onClick={dismissReauth} className="mt-3 font-gill text-xs text-ink/40 hover:text-ink/70">Cancel</button>
            </div>
          )}

          <div className="mt-8 space-y-3">
            <ExplorerNameSection
              profile={profile}
              setProfile={setProfile}
              withReauth={withReauth}
              open={openSection === 'name'}
              onToggle={() => toggle('name')}
            />

            <SidekickSection
              profile={profile}
              setProfile={setProfile}
              open={openSection === 'sidekick'}
              onToggle={() => toggle('sidekick')}
            />

            {isPasswordProvider && (
              <PasswordSection
                currentUser={currentUser!}
                open={openSection === 'password'}
                onToggle={() => toggle('password')}
              />
            )}

            <EmailSection
              isUnder13={under13}
              profile={profile}
              currentUser={currentUser}
              isPasswordProvider={isPasswordProvider}
              withReauth={withReauth}
              setNeedsReauth={setNeedsReauth}
              open={openSection === 'email'}
              onToggle={() => toggle('email')}
            />
          </div>

          {/* Chat + Sign out */}
          <div className="mt-6 space-y-3">
            <button
              onClick={() => setShowChat(true)}
              className="flex w-full items-center gap-4 rounded-2xl border border-white/10 bg-surface/60 px-5 py-4 text-left transition-colors hover:bg-white/5"
            >
              <span className="text-2xl">💬</span>
              <div>
                <p className="font-marker text-lg text-amber">Message Admin</p>
                <p className="font-gill text-sm text-ink/60">Chat with the OMore team</p>
              </div>
            </button>

            <button
              onClick={handleSignOut}
              className="w-full rounded-2xl border border-coral/30 bg-coral/5 py-3.5 font-marker text-base text-coral transition-colors hover:bg-coral/10"
            >
              Sign Out
            </button>
          </div>
        </div>
        <AppFooter />
      </div>

      {showChat && (
        <ChatModal
          userId={user.uid}
          explorerName={profile.explorerName || profile.displayName || 'Explorer'}
          onClose={() => setShowChat(false)}
        />
      )}
    </>
  );
}

// ─── Explorer Name Section ─────────────────────────────────────────────────────
interface ExplorerNameSectionProps {
  profile: Profile;
  setProfile: (profile: Profile) => void;
  withReauth: (action: () => Promise<void>) => Promise<void>;
  open: boolean;
  onToggle: () => void;
}

function ExplorerNameSection({ profile, setProfile, withReauth, open, onToggle }: ExplorerNameSectionProps) {
  const [nameInput, setNameInput] = useState(profile.explorerName ?? '');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<StatusMsg>(null);

  const lastChangedAt = profile.explorerNameLastChangedAt;
  const unlockAt = lastChangedAt
    ? new Date(lastChangedAt.toDate().getTime() + EXPLORER_NAME_COOLDOWN_DAYS * 24 * 60 * 60 * 1000)
    : null;
  const locked = !!unlockAt && unlockAt.getTime() > Date.now();

  async function handleSave() {
    const trimmed = nameInput.trim();
    if (!trimmed || trimmed === profile.explorerName) return;
    const modResult = moderateText(trimmed, true);
    if (!modResult.passed) {
      setMsg({ type: 'error', text: modResult.message ?? 'Explorer name not allowed.' });
      return;
    }
    await withReauth(async () => {
      setSaving(true);
      setMsg(null);
      try {
        await renameExplorerName({ profileId: profile.id, newExplorerName: trimmed });
        setProfile({ ...profile, explorerName: trimmed, displayName: trimmed });
        setMsg({ type: 'success', text: 'Explorer name updated!' });
      } catch (err: unknown) {
        const fbErr = err as { code?: string; message?: string };
        if (fbErr.code === 'functions/already-exists') {
          setMsg({ type: 'error', text: `"${trimmed}" is already taken. Try a different name.` });
        } else if (fbErr.code === 'functions/failed-precondition') {
          setMsg({ type: 'error', text: fbErr.message ?? `Name was recently changed. Please wait ${EXPLORER_NAME_COOLDOWN_DAYS} days.` });
        } else {
          setMsg({ type: 'error', text: 'Something went wrong. Please try again.' });
        }
      } finally {
        setSaving(false);
      }
    });
  }

  return (
    <SectionShell
      title="Explorer name"
      summary={profile.explorerName || 'Not set'}
      open={open}
      onToggle={onToggle}
    >
      {locked && unlockAt ? (
        <div className="rounded-2xl border border-white/10 bg-base/60 px-4 py-3 font-gill text-sm text-ink/75">
          You can change your explorer name again on{' '}
          <span className="font-semibold text-ink">
            {unlockAt.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
          </span>
          . Explorer names can be changed once every {EXPLORER_NAME_COOLDOWN_DAYS} days.
        </div>
      ) : (
        <div className="space-y-3">
          <p className="font-gill text-xs text-ink/50">
            Your explorer name appears throughout the app. It can be changed once every {EXPLORER_NAME_COOLDOWN_DAYS} days.
          </p>
          <input
            type="text"
            maxLength={30}
            value={nameInput}
            onChange={(e) => { setNameInput(e.target.value); setMsg(null); }}
            placeholder="Your explorer name"
            className={inputCls}
          />
          {msg && (
            <p className={`font-gill text-sm ${msg.type === 'success' ? 'text-mint' : 'text-coral'}`}>
              {msg.text}
            </p>
          )}
          <button
            onClick={handleSave}
            disabled={saving || !nameInput.trim() || nameInput.trim() === profile.explorerName}
            className="rounded-2xl bg-amber px-6 py-2.5 font-marker text-sm text-base shadow-glow-amber transition-opacity disabled:opacity-40"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    </SectionShell>
  );
}

// ─── Sidekick Section ─────────────────────────────────────────────────────────
interface SidekickSectionProps {
  profile: Profile;
  setProfile: (profile: Profile) => void;
  open: boolean;
  onToggle: () => void;
}

function SidekickSection({ profile, setProfile, open, onToggle }: SidekickSectionProps) {
  const prefs = profile.sidekickPreferences;
  const [nameInput, setNameInput] = useState(prefs?.name ?? 'KOKO');
  const [selectedTone, setSelectedTone] = useState<ToneOption>(prefs?.tone ?? 'cheerful');
  const [selectedAvatar, setSelectedAvatar] = useState(prefs?.avatarId ?? AVATARS[0].id);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<StatusMsg>(null);

  const currentAvatar = AVATARS.find((a) => a.id === selectedAvatar) ?? AVATARS[0];

  async function handleSave() {
    const trimmedName = nameInput.trim();
    if (!trimmedName) return;
    const modResult = moderateText(trimmedName, true);
    if (!modResult.passed) {
      setMsg({ type: 'error', text: modResult.message ?? 'Sidekick name not allowed.' });
      return;
    }
    setSaving(true);
    setMsg(null);
    try {
      await updateProfile({
        profileId: profile.id,
        fields: {
          sidekickPreferences: { name: trimmedName, tone: selectedTone, avatarId: selectedAvatar },
        },
      });
      setProfile({
        ...profile,
        sidekickPreferences: { name: trimmedName, tone: selectedTone, avatarId: selectedAvatar },
      });
      setMsg({ type: 'success', text: 'Sidekick updated!' });
    } catch {
      setMsg({ type: 'error', text: 'Something went wrong. Please try again.' });
    } finally {
      setSaving(false);
    }
  }

  const summary = prefs
    ? `${prefs.name} · ${prefs.tone}`
    : 'Not set';

  return (
    <SectionShell
      title="Sidekick"
      summary={summary}
      open={open}
      onToggle={onToggle}
    >
      <div className="space-y-5">
        {/* Name */}
        <div>
          <label className="mb-1.5 block font-gill text-sm text-amber/80">Sidekick name</label>
          <input
            type="text"
            maxLength={20}
            value={nameInput}
            onChange={(e) => { setNameInput(e.target.value); setMsg(null); }}
            placeholder="KOKO"
            className={inputCls}
          />
        </div>

        {/* Tone */}
        <div>
          <label className="mb-2 block font-gill text-sm text-amber/80">Tone</label>
          <div className="grid grid-cols-2 gap-2">
            {TONE_OPTIONS.map((option) => (
              <button
                key={option.tone}
                type="button"
                onClick={() => { setSelectedTone(option.tone); setMsg(null); }}
                className="rounded-xl p-3 text-left transition-all"
                style={
                  selectedTone === option.tone
                    ? { background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.5)' }
                    : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }
                }
              >
                <span className="text-xl">{option.emoji}</span>
                <p className="mt-1 font-semibold text-white text-sm">{option.label}</p>
                <p className="font-gill text-xs" style={{ color: 'rgba(232,234,240,0.5)' }}>{option.sample}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Avatar grid */}
        <div>
          <label className="mb-2 block font-gill text-sm text-amber/80">Avatar</label>
          <div className="grid grid-cols-6 gap-2">
            {AVATARS.map((avatar) => {
              const isSelected = selectedAvatar === avatar.id;
              return (
                <motion.button
                  key={avatar.id}
                  type="button"
                  whileHover={{ scale: 1.06 }}
                  whileTap={{ scale: 0.88 }}
                  onClick={() => { setSelectedAvatar(avatar.id); setMsg(null); }}
                  className="flex items-center justify-center rounded-xl transition-all"
                  style={
                    isSelected
                      ? { background: avatar.bg, border: `2px solid ${avatar.border}`, boxShadow: `0 0 12px ${avatar.border}`, padding: 5 }
                      : { background: 'rgba(255,255,255,0.04)', border: '2px solid rgba(255,255,255,0.08)', padding: 5 }
                  }
                >
                  <div style={{ width: 36, height: 36 }}>{avatar.svg()}</div>
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Preview */}
        <div
          className="flex items-center gap-3 rounded-xl p-3"
          style={{ background: currentAvatar.bg, border: `1px solid ${currentAvatar.border}` }}
        >
          <div style={{ width: 48, height: 48, flexShrink: 0 }}>{currentAvatar.svg()}</div>
          <div>
            <p className="font-marker text-white">{nameInput || 'KOKO'}</p>
            <p className="font-gill text-sm" style={{ color: 'rgba(232,234,240,0.5)' }}>
              {TONE_OPTIONS.find((t) => t.tone === selectedTone)?.sample}
            </p>
          </div>
        </div>

        {msg && (
          <p className={`font-gill text-sm ${msg.type === 'success' ? 'text-mint' : 'text-coral'}`}>{msg.text}</p>
        )}

        <button
          onClick={handleSave}
          disabled={saving || !nameInput.trim()}
          className="rounded-2xl bg-amber px-6 py-2.5 font-marker text-sm text-base shadow-glow-amber transition-opacity disabled:opacity-40"
        >
          {saving ? 'Saving…' : 'Save sidekick'}
        </button>
      </div>
    </SectionShell>
  );
}

// ─── Password Section ──────────────────────────────────────────────────────────
interface PasswordSectionProps {
  currentUser: User;
  open: boolean;
  onToggle: () => void;
}

function PasswordSection({ currentUser, open, onToggle }: PasswordSectionProps) {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<StatusMsg>(null);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);

  async function handleSave() {
    setMsg(null);
    if (newPw !== confirmPw) { setMsg({ type: 'error', text: 'New passwords do not match.' }); return; }
    if (newPw.length < 8) { setMsg({ type: 'error', text: 'New password must be at least 8 characters.' }); return; }
    setSaving(true);
    try {
      const credential = EmailAuthProvider.credential(currentUser.email!, currentPw);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, newPw);
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      setMsg({ type: 'success', text: 'Password updated successfully.' });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '';
      if (message.includes('wrong-password') || message.includes('invalid-credential')) {
        setMsg({ type: 'error', text: 'Current password is incorrect.' });
      } else {
        setMsg({ type: 'error', text: 'Failed to update password. Please try again.' });
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <SectionShell
      title="Password"
      summary="Change your account password"
      open={open}
      onToggle={onToggle}
    >
      <div className="space-y-3">
        <div>
          <label className="mb-1.5 block font-gill text-sm text-amber/80">Current password</label>
          <div className="relative">
            <input type={showCurrentPw ? 'text' : 'password'} value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} className={`${inputCls} pr-10`} />
            <button type="button" onClick={() => setShowCurrentPw((v) => !v)} className="absolute inset-y-0 right-0 flex items-center px-3 text-ink/40 hover:text-ink/70" aria-label={showCurrentPw ? 'Hide' : 'Show'}>
              <EyeIcon visible={showCurrentPw} />
            </button>
          </div>
        </div>
        <div>
          <label className="mb-1.5 block font-gill text-sm text-amber/80">New password</label>
          <div className="relative">
            <input type={showNewPw ? 'text' : 'password'} minLength={8} value={newPw} onChange={(e) => { setNewPw(e.target.value); setMsg(null); }} placeholder="At least 8 characters" className={`${inputCls} pr-10`} />
            <button type="button" onClick={() => setShowNewPw((v) => !v)} className="absolute inset-y-0 right-0 flex items-center px-3 text-ink/40 hover:text-ink/70" aria-label={showNewPw ? 'Hide' : 'Show'}>
              <EyeIcon visible={showNewPw} />
            </button>
          </div>
        </div>
        <div>
          <label className="mb-1.5 block font-gill text-sm text-amber/80">Confirm new password</label>
          <div className="relative">
            <input type={showConfirmPw ? 'text' : 'password'} value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} className={`${inputCls} pr-10`} />
            <button type="button" onClick={() => setShowConfirmPw((v) => !v)} className="absolute inset-y-0 right-0 flex items-center px-3 text-ink/40 hover:text-ink/70" aria-label={showConfirmPw ? 'Hide' : 'Show'}>
              <EyeIcon visible={showConfirmPw} />
            </button>
          </div>
        </div>
        {msg && (
          <p className={`font-gill text-sm ${msg.type === 'success' ? 'text-mint' : 'text-coral'}`}>{msg.text}</p>
        )}
        <button
          onClick={handleSave}
          disabled={saving || !currentPw || !newPw || !confirmPw}
          className="rounded-2xl bg-amber px-6 py-2.5 font-marker text-sm text-base shadow-glow-amber transition-opacity disabled:opacity-40"
        >
          {saving ? 'Updating…' : 'Update password'}
        </button>
      </div>
    </SectionShell>
  );
}

// ─── Email Section ─────────────────────────────────────────────────────────────
interface EmailSectionProps {
  isUnder13: boolean;
  profile: Profile;
  currentUser: User | null;
  isPasswordProvider: boolean;
  withReauth: (action: () => Promise<void>) => Promise<void>;
  setNeedsReauth: (v: boolean) => void;
  open: boolean;
  onToggle: () => void;
}

function EmailSection({ isUnder13, profile, currentUser, isPasswordProvider, withReauth, setNeedsReauth, open, onToggle }: EmailSectionProps) {
  const [emailInput, setEmailInput] = useState('');
  const [emailPw, setEmailPw] = useState('');
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState<StatusMsg>(null);

  const verifiedEmail = currentUser?.email ?? null;
  const pendingEmail = profile.pendingEmail ?? null;

  const summary = isUnder13
    ? profile.parentEmail || 'Managed by parent'
    : verifiedEmail || (pendingEmail ? `${pendingEmail} (pending)` : 'No recovery email on file');

  async function handleSend() {
    const trimmed = emailInput.trim();
    if (!trimmed) return;
    await withReauth(async () => {
      setSending(true);
      setMsg(null);
      try {
        await requestEmailChange({ profileId: profile.id, newEmail: trimmed });
        setMsg({ type: 'success', text: `Check your inbox at ${trimmed} to confirm the change.` });
        setEmailInput('');
        setEmailPw('');
      } catch (err: unknown) {
        const fbErr = err as { code?: string };
        if (fbErr.code === 'functions/already-exists') {
          setMsg({ type: 'error', text: 'This email is already registered to another account.' });
        } else if (fbErr.code === 'functions/unauthenticated') {
          setNeedsReauth(true);
        } else {
          setMsg({ type: 'error', text: 'Failed to send verification email. Please try again.' });
        }
      } finally {
        setSending(false);
      }
    });
  }

  return (
    <SectionShell
      title={isUnder13 ? 'Parent email' : 'Recovery email'}
      summary={summary}
      open={open}
      onToggle={onToggle}
    >
      {isUnder13 ? (
        <div className="space-y-3 font-gill text-sm text-ink/75">
          <p>
            Your parent's email is managed from the{' '}
            <span className="font-semibold text-ink">parent portal</span>. Ask your parent to sign in there if it
            needs to change.
          </p>
          {profile.parentEmail && (
            <div className="rounded-2xl border border-white/10 bg-base/60 px-4 py-3">
              <p className="text-xs uppercase tracking-widest text-ink/40">On file</p>
              <p className="mt-1 font-semibold text-ink">{profile.parentEmail}</p>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {verifiedEmail && (
            <div className="rounded-2xl border border-white/10 bg-base/60 px-4 py-3">
              <p className="font-gill text-[10px] uppercase tracking-widest text-ink/40">Current (verified)</p>
              <p className="mt-0.5 font-gill text-sm text-ink/90">{verifiedEmail}</p>
            </div>
          )}

          {pendingEmail && (
            <div className="rounded-2xl border border-yellow-500/30 bg-yellow-500/5 px-4 py-3">
              <p className="font-gill text-xs uppercase tracking-widest text-yellow-400">Pending verification</p>
              <p className="mt-1 font-gill text-sm font-medium text-ink">{pendingEmail}</p>
              <p className="mt-2 font-gill text-xs text-ink/60">
                We sent a confirmation link to this address. Click it within 1 hour to make it your recovery email.{' '}
                {verifiedEmail
                  ? 'Until then, your current recovery email stays in effect.'
                  : 'Until then, no recovery email is active on your account yet.'}
              </p>
            </div>
          )}

          {isPasswordProvider && (
            <div className="space-y-3">
              <p className="font-gill text-xs text-ink/50">
                We'll email a confirmation link to the new address. It doesn't become active until you click the link — this proves you own the inbox.
              </p>
              <div>
                <label className="mb-1.5 block font-gill text-sm text-amber/80">
                  {verifiedEmail || pendingEmail ? 'New email address' : 'Email address'}
                </label>
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => { setEmailInput(e.target.value); setMsg(null); }}
                  placeholder="you@example.com"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="mb-1.5 block font-gill text-sm text-amber/80">Current password</label>
                <input
                  type="password"
                  value={emailPw}
                  onChange={(e) => setEmailPw(e.target.value)}
                  className={inputCls}
                />
              </div>
              {msg && (
                <p className={`font-gill text-sm ${msg.type === 'success' ? 'text-mint' : 'text-coral'}`}>{msg.text}</p>
              )}
              <button
                onClick={handleSend}
                disabled={sending || !emailInput.trim()}
                className="w-full rounded-2xl py-3 font-marker text-base text-white shadow-lg transition-opacity disabled:opacity-40"
                style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #8B5CF6 100%)' }}
              >
                {sending ? 'Sending…' : 'Send verification link'}
              </button>
            </div>
          )}
        </div>
      )}
    </SectionShell>
  );
}
